import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { message } from 'antd';

const handleCancel = () => {
  const navigate = useNavigate();
  navigate('/companylist/');
};

// File size limit (500 KB)
const MAX_IMAGE_SIZE_KB = 500;

const compressImage = async (file) => {
  if (file.size / 1024 <= MAX_IMAGE_SIZE_KB) {
    return file;
  }

  try {
    // Compression options
    const options = {
      maxSizeMB: 0.5, // 500 KB limit
      maxWidthOrHeight: 1024, // Resize if necessary
      useWebWorker: true,
    };

    const compressedFile = await imageCompression(file, options);
    return new File([compressedFile], file.name, { type: compressedFile.type });
  } catch (error) {
    console.error('Compression error:', error);
    message.error('Image compression failed!');
    return null;
  }
};

const beforeUpload = async (file, itemName, itemCode) => {
  const isJpgOrPng = ['image/jpeg', 'image/png', 'image/jpg'].includes(
    file.type,
  );

  if (!isJpgOrPng) {
    message.error('Only JPG, JPEG, and PNG files are allowed!');
    return false; // Prevents file from being added
  }

  const compressedFile = await compressImage(file);
  if (!compressedFile) return false; // Prevent upload

  return renameFile(compressedFile, itemName, itemCode);
};

// Function to rename file
const renameFile = (file, itemName, itemCode) => {
  const extension = file.name.split('.').pop(); // Extract file extension
  const updatedItemName = itemName?.includes(' ')
    ? itemName.split(' ').join('_')
    : itemName;
  const newFileName = itemCode
    ? `${updatedItemName}_${itemCode}.${extension}`
    : `${updatedItemName}_${Date.now()}.${extension}`;

  return new File([file], newFileName, { type: file.type });
};

// const onPreview = async (file) => {
//   let src = file.url;
//   if (!src) {
//     src = await new Promise((resolve) => {
//       const reader = new FileReader();
//       reader.readAsDataURL(file.originFileObj);
//       reader.onload = () => resolve(reader.result);
//     });
//   }
//   const image = new Image();
//   image.src = src;
//   const imgWindow = window.open(src);
//   imgWindow?.document.write(image.outerHTML);
// };
const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const tagProcessedFile = (rawFile, processedFile) => {
  [rawFile, processedFile].forEach((target) => {
    if (!target) return;
    Object.defineProperty(target, '__processedFile', {
      value: processedFile,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  });
  return processedFile;
};

const imageOnChange = async (
  { fileList: newFileList },
  formData,
  setFormData,
) => {
  const processedFiles = await Promise.all(
    newFileList.map(async (fileWrapper) => {
      if (fileWrapper.status === 'removed') return fileWrapper;
      const done = { status: 'done', percent: 100 };

      const origin = fileWrapper.originFileObj;
      if (!origin) return { ...fileWrapper, ...done };

      // Set by beforeUpload: the cropped (and renamed/compressed) file. antd
      // only ever exposes the RAW pick as originFileObj, so swap it in here or
      // both the thumbnail and the saved image stay uncropped.
      let finalFile = origin.__processedFile;

      if (!finalFile) {
        finalFile = origin;
        if (origin.size > 512 * 1024 && origin.type?.startsWith('image/')) {
          try {
            finalFile = await imageCompression(origin, {
              maxSizeMB: 0.5,
              maxWidthOrHeight: 1024,
              useWebWorker: true,
              initialQuality: 0.8,
            });
          } catch (err) {
            console.error('Image compression failed:', err);
            finalFile = origin;
          }
        }
      }

      let thumbUrl = fileWrapper.thumbUrl;
      if (finalFile !== origin) {
        try {
          thumbUrl = await getBase64(finalFile);
        } catch (err) {
          console.error('Thumbnail generation failed:', err);
        }
      }

      return {
        ...fileWrapper,
        ...done,
        originFileObj: finalFile,
        name: finalFile.name || fileWrapper.name,
        size: finalFile.size,
        thumbUrl,
        url: undefined,
      };
    }),
  );

  setFormData({
    ...formData,
    fileList: processedFiles,
    images: processedFiles,
  });
};

// const imageOnChange = ({ fileList: newFileList }, formData, setFormData) => {
//   const updatedFileList = newFileList.filter((file) => {
//     if (file.status === 'removed') {
//       return false;
//     }
//     return (
//       file.type === 'image/jpeg' ||
//       file.type === 'image/png' ||
//       file.type === 'image/jpg'
//     );
//   });

//   return;

//   setFormData({
//     ...formData,
//     fileList: updatedFileList,
//   });

// };

const customRequest = ({ onSuccess }) => {
  onSuccess('done');
};

const onPreview = async (file, setPreviewImage, setPreviewOpen) => {
  if (!file.url && !file.preview) {
    file.preview = await getBase64(file.originFileObj);
  }
  setPreviewImage(file.url || file.preview);
  setPreviewOpen(true);
};

const onRemove = (file, formData, setFormData, form) => {
  setFormData({ ...formData, image: null, fileList: [] });
};

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e.file;
  }
  return e.file;
};

const goToCompanyEditForm = (id) => {
  return `/company/${id}/edit/`;
};

export {
  handleCancel,
  normFile,
  onPreview,
  goToCompanyEditForm,
  onRemove,
  beforeUpload,
  compressImage,
  imageOnChange,
  customRequest,
  tagProcessedFile,
};
